// Kontaktformular (/kontakt): Name, E-Mail, Wunschdatum und -uhrzeit für einen
// Beratungstermin. Die Daten werden per POST an /send-form im Backend geschickt
// (siehe BACKEND_URL in backend.js), dort per E-Mail weitergeleitet - es gibt
// keine eigene Datenbank-Speicherung auf dem Server für diese Anfragen.
//
// Ist eine Cookie-Einwilligung erteilt (userConsent), werden die Formulardaten
// zusätzlich für 24h im LocalStorage gehalten, damit der Nutzer sie nicht erneut
// eintippen muss, falls er die Seite verlässt und zurückkommt.
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { de } from "date-fns/locale";
import React from "react";
import Text from "../../components/Text/Text.jsx";
import Spacer from "../../components/Spacer/Spacer.jsx";
import Schritte from "../../components/NächsteSchritte/Schritte.jsx";
import { BACKEND_URL } from "../../backend.js";

import styles from "./Kontakt.module.css";

function Kontakt() {
  const [isLoading, setIsLoading] = React.useState(false);
  const [lastSubmit, setLastSubmit] = React.useState("");
  const [showSchritte, setShowSchritte] = React.useState(false);

  // Prüfe Einwilligung
  const userConsent = React.useMemo(() => {
    const saved = localStorage.getItem("user_consent");
    return saved === "true";
  }, []);

  // Initialisiere das Datenobjekt aus dem LocalStorage oder mit Defaultwerten
  const [dataObject, setDataObject] = React.useState(() => {
    if (userConsent) {
      const saved = localStorage.getItem("form_data");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return {
            ...parsed,
            date: parsed.date ? new Date(parsed.date) : null,
            time: parsed.time ? new Date(parsed.time) : null,
          };
        } catch {
          return { name: "", email: "", date: null, time: null };
        }
      }
    }
    return { name: "", email: "", date: null, time: null };
  });

  // Alert States
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertType, setAlertType] = React.useState("success");
  const [alertMessage, setAlertMessage] = React.useState("");

  // XSS-Prüfung
  function isSafeInput(input) {
    const pattern =
      /<script.*?>.*?<\/script>|javascript:|onerror=|onload=|<.*?on\w=.*?>/gi;
    return !pattern.test(input);
  }

  // LocalStorage prüfen und ggf. leeren
  function checkAndClearLocalStorage() {
    try {
      const timeexpired = localStorage.getItem("timeexpired");
      if (timeexpired) {
        const expiredDate = new Date(timeexpired);
        const today = new Date();
        if (expiredDate < today) {
          // Vor dem Löschen user_consent sichern
          const userConsentValue = localStorage.getItem("user_consent");
          localStorage.clear();
          // Nach dem Löschen user_consent wiederherstellen
          if (userConsentValue !== null) {
            localStorage.setItem("user_consent", userConsentValue);
          }
        }
      }
    } catch (error) {
      console.error("Fehler beim Zugriff auf localStorage:", error);
    }
  }

  // Speichert den letzten Submit-Zeitpunkt im LocalStorage, wenn userConsent gegeben ist
  React.useEffect(() => {
    if (userConsent) {
      checkAndClearLocalStorage();
      const saved = localStorage.getItem("last_submit");
      if (saved) {
        setLastSubmit(saved);
      }
    }
  }, [userConsent]);

  // Handler für alle Felder
  const handleChange = (field, value) => {
    const updated = { ...dataObject, [field]: value };
    setDataObject(updated);
    if (userConsent) {
      localStorage.setItem("form_data", JSON.stringify(updated));
      // timeexpired auf 24h ab jetzt setzen
      const expires = new Date();
      expires.setHours(expires.getHours() + 24);
      localStorage.setItem("timeexpired", expires.toISOString());
    }
  };

  // Submit Handler
  const handleSubmit = (e) => {
    e.preventDefault();

    const { name, email, date, time } = dataObject;

    // Felderprüfung: Sind alle Pflichtfelder belegt?
    if (!name || !email || !date || !time) {
      alert("Bitte alle Pflichtfelder ausfüllen.");
      return;
    }

    // XSS-Prüfung
    if (!isSafeInput(name) || !isSafeInput(email)) {
      alert("Ungültige Eingabe erkannt. Bitte überprüfe deine Eingaben.");
      return;
    }

    // Wunschtermin darf nicht in der Vergangenheit liegen.
    const today = new Date();
    if (new Date(date) < today.setHours(0, 0, 0, 0)) {
      alert("Das Datum darf nicht in der Vergangenheit liegen.");
      return;
    }

    const body = {
      name,
      email,
      date: date.toISOString().split("T")[0],
      time: time.toTimeString().slice(0, 5),
      company: "Foodosia",
    };

    setIsLoading(true);

    fetch(`${BACKEND_URL}/send-form`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (response.ok) {
          setAlertType("success");
          setAlertMessage("Formular erfolgreich gesendet!");
          setShowSchritte(true);
          if (userConsent) {
            localStorage.removeItem("form_data");
            localStorage.removeItem("timeexpired");
            const now = new Date();
            const formatted = now.toLocaleString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
            localStorage.setItem("last_submit", formatted);
            setLastSubmit(formatted);
          }
          setDataObject({ name: "", email: "", date: null, time: null });
        } else {
          return response.json().then((data) => {
            throw new Error(data.error || "Fehler beim Senden des Formulars.");
          });
        }
      })
      .catch((err) => {
        setAlertType("error");
        setAlertMessage(err.message || "Fehler beim Senden des Formulars.");
      })
      .finally(() => {
        setIsLoading(false);
        setAlertOpen(true);
      });
  };

  return (
    <div className={styles.contactContainer}>
      <Text
        title="Kontakt aufnehmen"
        body={
          <>
            <span
              onClick={() => setShowSchritte(true)}
              style={{
                color: "var(--primary-color)",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              Was passiert als nächstes?
            </span>{" "}
            Nehmen Sie Kontakt mit uns auf und schlagen Sie einen Wunschtermin
            vor. Wir melden uns zeitnah bei Ihnen, um einen persönlichen
            Beratungstermin zu vereinbaren.
          </>
        }
      />
      <Spacer space={"40px"} />
      {showSchritte && <Schritte onClose={() => setShowSchritte(false)} />}
      <form action="" onSubmit={handleSubmit}>
        <TextField
          label="Name"
          variant="outlined"
          value={dataObject.name}
          required
          onChange={(e) => handleChange("name", e.target.value)}
          className={styles.greenTextField}
        />
        <TextField
          label="E-Mail"
          variant="outlined"
          type="email"
          value={dataObject.email}
          required
          onChange={(e) => handleChange("email", e.target.value)}
          className={styles.greenTextField}
        />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
          <DatePicker
            id="date-picker"
            label="Wunschtermin (Datum)"
            value={dataObject.date}
            type="outlined"
            minDate={new Date()} // Verhindert Auswahl von vergangenen Daten
            required
            onChange={(newValue) => handleChange("date", newValue)}
          />
          <TimePicker
            id="time-picker"
            label="Wunschtermin (Uhrzeit)"
            value={dataObject.time}
            required
            onChange={(newValue) => handleChange("time", newValue)}
          />
        </LocalizationProvider>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          sx={{
            backgroundColor: "var(--primary-color)",
            color: "#fff",
            padding: "10px 20px",
            borderRadius: "5px",
            fontWeight: 500,
            textAlign: "center",
            fontSize: "16px",
            border: "none",
            boxShadow: "none",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "var(--primary-color-hover)",
              border: "none",
              boxShadow: "none",
              color: "#fff",
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            "Abschicken"
          )}
        </Button>
        {lastSubmit && (
          <p className={styles.sentMessage}>
            Zuletzt abgesendet am {lastSubmit}.
          </p>
        )}
        <div
          style={{
            color: "#888",
            fontSize: "13px",
            textAlign: "center",
            marginTop: "18px",
            width: "100%",
            lineHeight: 1.5,
          }}
        >
          Indem du das Formular abschickst, erklärst du dich damit
          einverstanden, dass wir deine angegebenen Daten verarbeiten und dich
          kontaktieren.
          <br />
          Lies dir unsere{" "}
          <a
            href="/datenschutzerklaerung"
            style={{ color: "#888", textDecoration: "underline" }}
          >
            Datenschutzerklärung
          </a>{" "}
          und unsere{" "}
          <a href="/agb" style={{ color: "#888", textDecoration: "underline" }}>
            allgemeinen Geschäftsbedingungen
          </a>{" "}
          für weitere Informationen durch.
        </div>
      </form>
    </div>
  );
}

export default Kontakt;
